import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { EventConfig, CanvasContext } from '../interfaces/event-script.models';
import { BuilderNode } from '../interfaces/builder-screen.models';
import { EventScriptService } from '../services/event-script.service';

/**
 * Editor principal de eventos (dual-mode: simple + expert)
 * Usado como drawer/modal para configurar eventos
 */
@Component({
  selector: 'app-event-script-editor',
  templateUrl: './event-script-editor.component.html',
  styleUrls: ['./event-script-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class EventScriptEditorComponent implements OnInit {
  @Input() visible = false;
  @Input() componentNode: BuilderNode | null = null;
  @Input() canvasContext: CanvasContext | null = null;
  @Input() eventConfig: EventConfig | null = null; // Para edição
  @Input() eventType: 'onChange' | 'onInit' = 'onChange';
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() save = new EventEmitter<EventConfig>();
  @Output() cancel = new EventEmitter<void>();

  currentMode: 'simple' | 'expert' = 'simple';
  currentEventConfig: EventConfig | null = null;
  availableIds: string[] = [];

  constructor(
    private eventScriptService: EventScriptService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Se está editando, usar config existente
    if (this.eventConfig) {
      this.currentEventConfig = { ...this.eventConfig };
      this.currentMode = this.eventConfig.mode;
    } else {
      // Criar novo evento
      this.currentEventConfig = this.eventScriptService.createEmptyEvent(this.eventType);
    }

    // Extrair IDs disponíveis do contexto
    if (this.canvasContext) {
      this.availableIds = [
        ...this.canvasContext.availableTabs.map(t => t.id),
        ...this.canvasContext.availablePanels.map(p => p.id),
        ...this.canvasContext.availableFields.map(f => f.id)
      ];
    }
  }

  /**
   * Quando o modo muda (simple <-> expert)
   */
  onModeChange(mode: 'simple' | 'expert'): void {
    this.currentMode = mode;
    
    // Atualizar eventConfig para o novo modo
    if (this.currentEventConfig) {
      this.currentEventConfig.mode = mode;
    }

    this.cdr.markForCheck();
  }

  /**
   * Quando configuração é criada no modo simples
   */
  onSimpleModeConfig(config: EventConfig): void {
    this.currentEventConfig = config;
    this.cdr.markForCheck();
  }

  /**
   * Quando configuração muda no modo expert
   */
  onExpertModeConfig(config: EventConfig): void {
    this.currentEventConfig = config;
    this.cdr.markForCheck();
  }

  /**
   * Salva o evento
   */
  onSave(): void {
    if (!this.currentEventConfig) {
      return;
    }

    // Validar antes de salvar
    const validation = this.eventScriptService.validateEvent(
      this.currentEventConfig,
      this.availableIds
    );

    if (!validation.valid) {
      // Mostrar erros de validação
      console.error('Erro de validação:', validation.errors);
      // TODO: Mostrar mensagem de erro ao usuário
      return;
    }

    this.save.emit(this.currentEventConfig);
    this.onClose();
  }

  /**
   * Cancela a edição
   */
  onCancel(): void {
    this.cancel.emit();
    this.onClose();
  }

  /**
   * Fecha o drawer
   */
  onClose(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  /**
   * Obtém título do drawer
   */
  get drawerTitle(): string {
    if (this.eventConfig) {
      return 'Editar Evento';
    }
    return `Adicionar Evento - ${this.eventType === 'onChange' ? 'Ao Mudar' : 'Ao Inicializar'}`;
  }

  /**
   * Verifica se pode salvar
   */
  canSave(): boolean {
    return !!this.currentEventConfig;
  }
}



